import { useState, useRef } from "react";
import { toast } from "sonner";
import { Circle, Grid2x2, Play, VolumeX } from "lucide-react";
import { ttsAudioUrl } from "@/lib/apiClient";
import { resolveAudioSrc } from "@/lib/offlineCache";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Reading & Listening — Individual (tap one item → TTS/narrated audio) or
 * Group (tap an admin-defined group of items → the one uploaded/recorded
 * audio covering that whole group). `hasSections`/`selectedSection` come
 * from ReadingMode's shared section picker (kept in sync with Test mode) —
 * both sub-modes scope their content to it.
 */
export default function LearnReadingListening({ module: m, hasSections, selectedSection }) {
  const { t } = useI18n();
  const [subMode, setSubMode] = useState("individual"); // "individual" | "group"
  const [playingIdx, setPlayingIdx] = useState(null);
  const [playingGroupId, setPlayingGroupId] = useState(null);
  const [fontStep, setFontStep] = useState(() => {
    const v = Number(localStorage.getItem("qf:font-step"));
    return Number.isFinite(v) && v >= -1 && v <= 2 ? v : 0;
  });
  const audioRef = useRef(null);
  const fontSizeClass = ["text-xl sm:text-2xl", "text-2xl sm:text-3xl", "text-3xl sm:text-4xl", "text-4xl sm:text-5xl"][fontStep + 1] || "text-2xl sm:text-3xl";
  // Group lines run bigger than individual items by default (one tier up
  // at each step) since a whole line of a group is meant to be read as a
  // single glance/breath, not tapped one letter at a time.
  const groupFontSizeClass = ["text-2xl sm:text-3xl", "text-3xl sm:text-4xl", "text-4xl sm:text-5xl", "text-5xl sm:text-6xl"][fontStep + 1] || "text-3xl sm:text-4xl";

  // Plays in the same tick as the click — no `await` before `.play()`.
  // Two things depend on that: (1) Safari/iOS only lets a fresh <audio>
  // start without extra prompts when `.play()` is still inside the
  // original tap's call stack; an `await` in between breaks that chain
  // and Safari silently blocks playback — no error, it just stays quiet.
  // (2) it's simply faster, which is the whole point of tap-to-hear.
  // The offline-cache lookup (IndexedDB, inherently async) only matters
  // when there's no network to fall back to, so it's skipped entirely
  // whenever the browser reports itself online.
  const playSrc = (onlineSrc, resetPlaying) => {
    const audio = audioRef.current;
    if (!audio) return;
    const start = (src) => {
      audio.src = src;
      audio.play().catch(() => {
        resetPlaying();
        toast.error(t("rd.audio_play_failed"));
      });
    };
    if (navigator.onLine) start(onlineSrc);
    else resolveAudioSrc(onlineSrc).then(start);
  };

  const playItem = (i) => {
    const it = m.items[i];
    if (!it?.ar || !audioRef.current) return;
    setPlayingGroupId(null);
    setPlayingIdx(i);
    const onlineSrc = it.narrated_audio_url
      ? `${process.env.REACT_APP_BACKEND_URL}${it.narrated_audio_url}`
      : ttsAudioUrl(it.ar);
    playSrc(onlineSrc, () => setPlayingIdx((cur) => (cur === i ? null : cur)));
  };

  const playGroup = (g) => {
    if (!g.narrated_audio_url || !audioRef.current) return;
    setPlayingIdx(null);
    setPlayingGroupId(g.id);
    const onlineSrc = `${process.env.REACT_APP_BACKEND_URL}${g.narrated_audio_url}`;
    playSrc(onlineSrc, () => setPlayingGroupId((cur) => (cur === g.id ? null : cur)));
  };

  const allGroups = m.groups || [];
  const inSection = (idx) => !hasSections || m.items[idx]?.rule === selectedSection;
  const groups = hasSections
    ? allGroups.filter((g) => (g.item_indices || []).every(inSection))
    : allGroups;
  // Individual mode scopes to the same shared section as Group mode and
  // Test — previously it ignored section scoping entirely.
  const visibleItems = m.items
    .map((it, i) => ({ it, i }))
    .filter(({ i }) => inSection(i));

  return (
    <div className="rounded-3xl bg-white border border-[#E7E0D2] p-6 sm:p-10 parchment">
      {/* Sticky control bar — mirrors Test mode's sticky toolbar so the
          sub-mode/font controls stay reachable while scrolling the grid. */}
      <div className="sticky sticky-below-header z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-2 pb-2 mb-5 bg-[#FDFBF7]/95 backdrop-blur supports-[backdrop-filter]:bg-[#FDFBF7]/85" data-testid="sticky-toolbar">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white border border-[#E7E0D2] shadow-sm p-3">
          <div className="inline-flex items-center rounded-full border border-[#E7E0D2] overflow-hidden" data-testid="reading-listening-sub-toggle">
            <button
              data-testid="sub-mode-individual"
              onClick={() => setSubMode("individual")}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium transition ${
                subMode === "individual" ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:text-[#1F2937]"
              }`}>
              <Circle className="w-4 h-4" /> {t("rd.sub_individual")}
            </button>
            <button
              data-testid="sub-mode-group"
              onClick={() => setSubMode("group")}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium transition ${
                subMode === "group" ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:text-[#1F2937]"
              }`}>
              <Grid2x2 className="w-4 h-4" /> {t("rd.sub_group")}
            </button>
          </div>
          <div className="inline-flex items-center rounded-full border border-[#E7E0D2] overflow-hidden" data-testid="font-size-control" title={t("rd.font_title")}>
            <button data-testid="font-smaller" onClick={() => setFontStep((s) => Math.max(-1, s - 1))}
              className="px-2.5 py-2 text-sm text-[#4B5563] hover:bg-[#ECFDF5] hover:text-[#1F2937] transition" aria-label={t("rd.smaller")}>A−</button>
            <span className="px-1.5 text-[10px] text-[#4B5563] tabular-nums">{t("rd.font_size_label")}</span>
            <button data-testid="font-larger" onClick={() => setFontStep((s) => Math.min(2, s + 1))}
              className="px-2.5 py-2 text-sm text-[#4B5563] hover:bg-[#ECFDF5] hover:text-[#1F2937] transition" aria-label={t("rd.larger")}>A+</button>
          </div>
        </div>
      </div>

      {subMode === "individual" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" style={{ direction: "rtl" }} data-testid="individual-content-area">
          {visibleItems.map(({ it, i }) => {
            const isPlaying = playingIdx === i;
            return (
              <button
                key={`${it.ar}-${i}`}
                type="button"
                onClick={() => playItem(i)}
                data-testid={`item-${i}`}
                className={`rounded-2xl bg-[#FDFBF7] border-2 p-5 text-center transition cursor-pointer ${
                  isPlaying ? "border-[#064E3B] bg-[#ECFDF5]" : "border-[#E7E0D2] hover:border-[#064E3B]"
                }`}>
                <div className={`font-quran ${fontSizeClass} text-[#1F2937] leading-loose`} dir="rtl" lang="ar">{it.ar}</div>
                <div className="mt-2 text-xs text-[#4B5563] italic flex items-center justify-center gap-1.5" dir="ltr">
                  {isPlaying && <span className="w-1.5 h-1.5 rounded-full bg-[#064E3B] animate-pulse" />}
                  {it.translit}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div data-testid="group-content-area">
          {groups.length === 0 ? (
            <div className="text-center text-sm text-[#4B5563] py-10" data-testid="group-empty-state">{t("rd.group_none_yet")}</div>
          ) : (
            <div className="flex flex-col gap-3">
              {groups.map((g) => {
                const isPlaying = playingGroupId === g.id;
                const hasAudio = Boolean(g.narrated_audio_url);
                const memberItems = (g.item_indices || []).map((idx) => m.items[idx]).filter(Boolean);
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => hasAudio && playGroup(g)}
                    disabled={!hasAudio}
                    data-testid={`group-${g.id}`}
                    className={`w-full rounded-2xl border-2 p-5 sm:p-6 text-right transition ${
                      !hasAudio ? "border-[#E7E0D2] opacity-60 cursor-not-allowed" :
                      isPlaying ? "border-[#064E3B] bg-[#ECFDF5] cursor-pointer" : "border-[#E7E0D2] hover:border-[#064E3B] cursor-pointer"
                    }`}>
                    <div className="flex items-center justify-between mb-3" dir="ltr">
                      <span className="text-xs font-medium text-[#1F2937]">{g.label || t("rd.group_label_fallback", { n: g.id })}</span>
                      {hasAudio ? (
                        <span className="inline-flex items-center gap-1 text-[10px] text-[#064E3B]">
                          {isPlaying && <span className="w-1.5 h-1.5 rounded-full bg-[#064E3B] animate-pulse" />}
                          <Play className="w-3.5 h-3.5" />
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] text-[#9CA3AF]" title={t("rd.group_no_audio")}>
                          <VolumeX className="w-3.5 h-3.5" /> {t("rd.group_no_audio")}
                        </span>
                      )}
                    </div>
                    {/* One line per group — no wrap; scrolls horizontally on
                        overflow rather than breaking onto a second line, so
                        the whole group reads as a single glance/breath. Extra
                        vertical room (min-height + padding) keeps Arabic
                        diacritics from being clipped at larger font sizes. */}
                    <div className="flex items-center gap-4 overflow-x-auto whitespace-nowrap py-3 min-h-[4.5rem] sm:min-h-[5.5rem]" dir="rtl">
                      {memberItems.map((it, i) => (
                        <span key={i} className={`font-quran ${groupFontSizeClass} text-[#1F2937] leading-loose flex-shrink-0`} lang="ar">{it.ar}</span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <audio
        ref={audioRef}
        onEnded={() => { setPlayingIdx(null); setPlayingGroupId(null); }}
        onError={() => {
          // Fires for a genuinely broken/unreachable file, distinct from
          // play() being refused above (e.g. autoplay policy) — both were
          // previously silent failures with zero user-visible feedback.
          setPlayingIdx(null);
          setPlayingGroupId(null);
          toast.error(t("rd.audio_play_failed"));
        }}
        preload="none" />
    </div>
  );
}
